import { useEffect, useMemo, useState } from 'react';
import { getPublicTools, type ToolDto } from '@/api/tool';

interface TutorialStep {
  title: string;
  desc: string;
}

interface TutorialItem {
  id: string;
  phase: string;
  title: string;
  summary: string;
  steps: TutorialStep[];
}

function readText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => readText(item)).filter(Boolean);
}

function buildTutorialsFromTools(list: ToolDto[]): TutorialItem[] {
  return list
    .map((tool, index) => {
      const id = String(tool.id ?? '');
      if (!id) return null;

      const installSteps = readStringArray((tool as any)?.detail?.install);
      const usageSteps = readStringArray((tool as any)?.detail?.usage);
      const merged = [...installSteps, ...usageSteps].filter(Boolean);
      if (merged.length === 0) return null;

      const steps = merged.slice(0, 8).map((text, idx) => ({
        title: `步骤 ${idx + 1}`,
        desc: text,
      }));

      return {
        id,
        phase: `教程 ${index + 1}`,
        title: readText(tool.name) || `工具 #${id}`,
        summary: readText(tool.description) || '暂无描述',
        steps,
      };
    })
    .filter((item): item is TutorialItem => Boolean(item))
    .slice(0, 6);
}

function StepCard({ step, index, isLast }: { step: TutorialStep; index: number; isLast: boolean }) {
  const [open, setOpen] = useState(index === 0);

  return (
    <div className="relative flex gap-4">
      {!isLast && <div className="absolute left-5 top-10 bottom-0 w-px bg-gray-100"></div>}
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-gray-900 text-white text-sm font-bold z-10">
        {index + 1}
      </div>
      <div className="flex-1 pb-6">
        <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center justify-between gap-3 cursor-pointer group">
          <h4 className="font-semibold text-gray-900 text-sm group-hover:text-[#f97316] transition-colors">{step.title}</h4>
          <i className={`ri-arrow-down-s-line text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}></i>
        </button>
        {open && <p className="mt-3 text-gray-600 text-sm leading-relaxed">{step.desc}</p>}
      </div>
    </div>
  );
}

export default function TutorialSection() {
  const [tools, setTools] = useState<ToolDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    getPublicTools()
      .then((res) => {
        if (!alive) return;
        const list = Array.isArray(res?.list) ? res.list : [];
        setTools(list);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('Failed to load tutorials from real tool data:', err);
        setError(err instanceof Error ? err.message : '加载教程失败');
        setTools([]);
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const tutorials = useMemo(() => buildTutorialsFromTools(tools), [tools]);

  useEffect(() => {
    if (!activeId && tutorials.length > 0) {
      setActiveId(tutorials[0].id);
    }
    if (activeId && tutorials.every((item) => item.id !== activeId)) {
      setActiveId(tutorials[0]?.id || '');
    }
  }, [activeId, tutorials]);

  const active = tutorials.find((item) => item.id === activeId) || tutorials[0] || null;

  return (
    <section className="py-20 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f97316]/10 text-[#f97316] text-sm font-medium mb-4">
            <i className="ri-book-open-line"></i>
            使用指南
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">安装与使用教程</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed">
            内容根据当前工具已维护的安装与使用步骤整理展示。
          </p>
        </div>

        {loading ? (
          <div className="bg-[#f8fafc] rounded-2xl border border-gray-100 p-10 text-center text-gray-500">正在加载教程数据...</div>
        ) : error ? (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-6 text-sm text-red-500">教程加载失败：{error}</div>
        ) : tutorials.length === 0 ? (
          <div className="bg-[#f8fafc] rounded-2xl border border-gray-100 p-10 text-center">
            <i className="ri-file-list-3-line text-4xl text-gray-300 block mb-3"></i>
            <p className="text-gray-500 text-sm">当前暂无可展示教程</p>
            <p className="text-gray-400 text-xs mt-1">请在工具管理中为工具填写“使用指南”后自动展示</p>
          </div>
        ) : (
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-80 flex-shrink-0 space-y-2">
              {tutorials.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveId(item.id)}
                  className={`w-full text-left p-4 rounded-xl border transition-all cursor-pointer ${
                    activeId === item.id
                      ? 'border-[#f97316] bg-[#f97316]/5'
                      : 'border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-xs text-gray-400 mb-1">{item.phase}</div>
                      <p className="font-semibold text-gray-900 text-sm leading-snug">{item.title}</p>
                    </div>
                    {activeId === item.id && <i className="ri-arrow-right-s-line text-[#f97316]"></i>}
                  </div>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">{item.summary}</p>
                </button>
              ))}
            </div>

            <div className="flex-1 min-w-0">
              {active && (
                <div className="bg-[#f8fafc] rounded-2xl p-6 md:p-8 border border-gray-100">
                  <div className="mb-6">
                    <p className="text-sm text-gray-400 mb-2">{active.phase}</p>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{active.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{active.summary}</p>
                  </div>

                  <div className="space-y-0">
                    <h4 className="text-sm font-semibold text-gray-700 mb-5 flex items-center gap-2">
                      <i className="ri-list-check-2 text-[#f97316]"></i>
                      操作步骤（共 {active.steps.length} 步）
                    </h4>
                    {active.steps.map((step, i) => (
                      <StepCard key={`${active.id}-${i}`} step={step} index={i} isLast={i === active.steps.length - 1} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
